import type { IconName } from '../Icon.js'
import type { Tone } from '../ui.js'
import type { Job } from './types.js'

/**
 * How a service reads on the job card.
 *
 * The glyph and the tone are columns on `ServiceType`, so the office can add
 * "Bird proofing" in Settings and it arrives on the card looking like the rest
 * of them without a deployment. Both fall back rather than throwing: an enum
 * value added to the database before this file knows about it should draw a
 * plain service row, not a blank tile.
 */

export type JobServiceRow = NonNullable<Job['services']>[number]

const ICONS: Record<string, IconName> = {
  TERMITE: 'termite',
  ROACH: 'roach',
  RODENT: 'rodent',
  INSECT: 'insect',
  BIRD: 'bird',
  BEE: 'bee',
  GENERAL: 'spray',
}

const TONES: Record<string, Tone> = {
  // The stored colours predate the brand rule (decision 110); each reads as
  // the brand tone nearest its meaning rather than as a hue.
  NEUTRAL: 'neutral',
  BLUE: 'outline',
  GREEN: 'green',
  AMBER: 'dark',
  RED: 'red',
  PURPLE: 'dark',
}

/**
 * The darker step of the row's own hue, for the solid tile.
 *
 * `toneStyles` gives the tint and the ring; the tile is the one thing it has no
 * entry for, because nothing else on the card fills a shape with a tone.
 */
const TILES: Record<Tone, string> = {
  neutral: 'bg-ink-600',
  outline: 'bg-ink-900',
  green: 'bg-brand-700',
  dark: 'bg-ink-900',
  red: 'bg-red-700',
}

export function serviceIcon(icon: string): IconName {
  return ICONS[icon] ?? 'spray'
}

export function serviceTone(tone: string): Tone {
  return TONES[tone] ?? 'neutral'
}

export function serviceTile(tone: Tone): string {
  return TILES[tone]
}

/**
 * What state a service is in, in the words the row uses.
 *
 * Deliberately derived from the report rather than stored. `completedAt` on the
 * service is set by trigger when the report is submitted, so a service cannot
 * read as outstanding while its report is signed — and a row that decided for
 * itself would be the second opinion that disagrees.
 */
export function serviceStatus(service: JobServiceRow): {
  label: string
  done: boolean
  /** The label on the button inside the expanded brief. */
  action: string
} {
  const minutes = service.estimatedMinutes
  const estimate = minutes === null ? null : `${minutes} min`

  if (service.serviceType.formId === null) {
    return {
      label: [estimate, 'No report needed'].filter(Boolean).join(' · '),
      done: service.completedAt !== null,
      action: 'Open the service',
    }
  }

  if (service.formResponse === null) {
    return {
      label: [estimate, 'Report not started'].filter(Boolean).join(' · '),
      done: false,
      action: 'Start the report',
    }
  }

  if (service.formResponse.status === 'DRAFT') {
    return {
      label: [estimate, 'Report in progress'].filter(Boolean).join(' · '),
      done: false,
      action: 'Carry on with the report',
    }
  }

  return { label: 'Report complete', done: true, action: 'Read the report back' }
}

/** Services still to be finished, for the count beside the Work tab. */
export function openServices(job: Job): number {
  return (job.services ?? []).filter((service) => !serviceStatus(service).done).length
}

/**
 * How a ticket reads, from the standing the server derived.
 *
 * The badge word is short because it sits at the end of a row on a phone. The
 * sentence underneath is where the detail goes, and it says the date rather than
 * only "expired", because the next question is always when.
 *
 * Delegated to `lib/licences.ts` so the job card and the certifications screens
 * cannot end up calling the same standing two different things.
 */
export { standingBadge as ticketBadge } from '../../lib/licences.js'

/** The sentence under a ticket's name. */
export function ticketDetail(ticket: {
  standing: string
  daysLeft: number | null
  expiresAt: string | Date | null
  isMandatory: boolean
  licenceType: { authority: string | null }
}): string {
  const authority = ticket.licenceType.authority

  switch (ticket.standing) {
    case 'held':
      return ticket.daysLeft === null
        ? [authority, 'Signed off, no expiry'].filter(Boolean).join(' · ')
        : `Current for another ${ticket.daysLeft} days`
    case 'due':
      if (ticket.daysLeft === 0) return 'Expires today. Renew it before the next one of these.'
      if (ticket.daysLeft === 1) return 'Expires tomorrow. Renew it before the next one of these.'
      return `Expires in ${ticket.daysLeft} days. Renewals take weeks, so start now.`
    case 'expired':
      return 'Lapsed. You cannot do this work until it is renewed.'
    case 'undated':
      return 'Held, but no expiry date is recorded. Ask the office to put one in.'
    default:
      return ticket.isMandatory
        ? 'You do not hold this, and this service needs it.'
        : 'You do not hold this. Worth having for this kind of work.'
  }
}
